import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import {
  PagedResponse,
  Transaction,
  TransactionCsvImportResponse,
  TransactionCsvExportAcceptedResponse,
  TransactionCsvExportRequest,
  TransactionNoteSuggestion,
  TransactionPagedRequest,
  TransactionType,
  UpsertTransactionRequest,
} from '@core/models';
import { endpoints, environment } from '@environment';
import { Observable } from 'rxjs';
import { suppressGlobalErrorFeedback } from '@core/interceptors';

@Injectable({ providedIn: 'root' })
export class TransactionService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.apiUrl}/${endpoints.v1.transactions}`;

  getById(id: string): Observable<Transaction> {
    return this.http.get<Transaction>(`${this.baseUrl}/${id}`, { context: suppressGlobalErrorFeedback() });
  }

  getPaged(request: TransactionPagedRequest): Observable<PagedResponse<Transaction>> {
    return this.http.post<PagedResponse<Transaction>>(`${this.baseUrl}/paged`, request, { context: suppressGlobalErrorFeedback() });
  }

  getNoteSuggestions(
    transactionType: TransactionType,
    search?: string,
    limit = 10
  ): Observable<TransactionNoteSuggestion[]> {
    let params = new HttpParams()
      .set('transactionType', transactionType)
      .set('limit', limit);

    if (search?.trim()) {
      params = params.set('search', search.trim());
    }

    return this.http.get<TransactionNoteSuggestion[]>(
      `${this.baseUrl}/note-suggestions`,
      { params, context: suppressGlobalErrorFeedback() }
    );
  }

  create(payload: UpsertTransactionRequest): Observable<Transaction> {
    return this.http.post<Transaction>(this.baseUrl, payload, { context: suppressGlobalErrorFeedback() });
  }

  update(id: string, payload: UpsertTransactionRequest): Observable<Transaction> {
    return this.http.put<Transaction>(`${this.baseUrl}/${id}`, payload, { context: suppressGlobalErrorFeedback() });
  }

  delete(id: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${id}`, { context: suppressGlobalErrorFeedback() });
  }

  previewCsvImport(file: File): Observable<TransactionCsvImportResponse> {
    return this.http.post<TransactionCsvImportResponse>(
      `${this.baseUrl}/import-csv/preview`,
      this.buildCsvImportFormData(file),
      { context: suppressGlobalErrorFeedback() }
    );
  }

  commitCsvImport(file: File): Observable<TransactionCsvImportResponse> {
    return this.http.post<TransactionCsvImportResponse>(
      `${this.baseUrl}/import-csv/commit`,
      this.buildCsvImportFormData(file),
      { context: suppressGlobalErrorFeedback() }
    );
  }

  exportCsv(request: TransactionCsvExportRequest): Observable<TransactionCsvExportAcceptedResponse> {
    return this.http.post<TransactionCsvExportAcceptedResponse>(`${this.baseUrl}/export-csv`, request, { context: suppressGlobalErrorFeedback() });
  }

  private buildCsvImportFormData(file: File): FormData {
    const formData = new FormData();
    formData.append('file', file);
    return formData;
  }
}
